import * as React from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Alert,
  Button,
  DateInput,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
  toast,
} from '@jumpstone/ui-react';
import type {
  TerminateEmployeeInput,
  TerminationMutationFailure,
  TerminationMutationResult,
} from '../../data/use_termination';

export interface TerminateConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee_id: string;
  employee_name: string;
  /**
   * When `null`, no Sri Lankan / Indonesian post-termination step follows —
   * the confirm dialog runs `terminate` itself. When non-null, this is
   * called INSTEAD of `terminate` and is expected to open the final-pay
   * drawer, which will own the actual `terminateEmployee` call (LK
   * `postGratuityTermination` posts the settlement journal, then
   * `terminateEmployee` flips the status; ID `postPesangon` handles both in
   * one call). The dialog's own captured `termination_date` + `reason`
   * are passed through so the drawer's journal date and audit-log reason
   * match what the operator picked here — never a re-derived
   * `today_iso()` and never a silently-discarded reason (adversarial-
   * review finding #1).
   */
  onOpenFinalPay: ((draft: TerminationDraft) => void) | null;
  terminate:
    | ((
        input: TerminateEmployeeInput,
      ) => Promise<TerminationMutationResult<{ employee_id: string }>>)
    | undefined;
  onSuccess: () => void;
}

function today_iso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Confirmation dialog for the terminate action on the employee profile.
 * Two paths:
 *
 *   1. `onOpenFinalPay` is non-null (LK/ID) — the dialog collects the
 *      termination_date + reason, then hands off to the final-pay drawer;
 *      the drawer owns the actual `terminateEmployee` call so the
 *      settlement journal and the status flip land in the same interaction.
 *
 *   2. `onOpenFinalPay` is null (`OTHER` jurisdiction, or a caller that
 *      doesn't want a settlement step) — this dialog itself calls
 *      `terminate`.
 *
 * The "already terminated" backend rejection surfaces INLINE inside this
 * dialog rather than a toast — a concurrent-click race is not a user error
 * and doesn't warrant a red banner.
 */
export function TerminateConfirmDialog({
  open,
  onOpenChange,
  employee_id,
  employee_name,
  onOpenFinalPay,
  terminate,
  onSuccess,
}: TerminateConfirmDialogProps): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [termination_date, set_termination_date] = React.useState(today_iso());
  const [reason, set_reason] = React.useState('');
  const [failure, set_failure] =
    React.useState<TerminationMutationFailure | null>(null);
  const [is_submitting, set_is_submitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      set_termination_date(today_iso());
      set_reason('');
      set_failure(null);
      set_is_submitting(false);
    }
  }, [open]);

  async function handle_confirm(): Promise<void> {
    set_failure(null);
    if (onOpenFinalPay) {
      // Two-step flow: the drawer will own the terminateEmployee call.
      // Pass through the exact date + reason the operator chose here so
      // the settlement journal and the terminateEmployee audit log line
      // up (rather than the drawer re-deriving `today_iso()`, which is a
      // real bug when the operator changed the date in this dialog).
      const draft: TerminationDraft = {
        termination_date,
        reason: reason.trim() === '' ? undefined : reason.trim(),
      };
      onOpenChange(false);
      onOpenFinalPay(draft);
      return;
    }
    if (!terminate) return;
    set_is_submitting(true);
    const result = await terminate({
      employee_id,
      termination_date,
      reason: reason.trim() === '' ? undefined : reason.trim(),
    });
    set_is_submitting(false);
    if (result.outcome === 'success') {
      toast.success(t('employees.terminate.success', { name: employee_name }));
      onOpenChange(false);
      onSuccess();
      return;
    }
    set_failure(result.failure);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="terminate-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{t('employees.terminate.confirm_title')}</DialogTitle>
          <DialogDescription>
            {t('employees.terminate.confirm_description', {
              name: employee_name,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1">
            <label htmlFor="terminate-date" className="nv-label">
              {t('employees.terminate.date_label')}
            </label>
            <DateInput
              id="terminate-date"
              className="w-full"
              value={termination_date}
              onChange={(e) => {
                set_termination_date(e.target.value);
              }}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="terminate-reason" className="nv-label">
              {t('employees.terminate.reason_label')}
            </label>
            <Textarea
              id="terminate-reason"
              rows={3}
              maxLength={500}
              placeholder={t('employees.terminate.reason_placeholder')}
              value={reason}
              onChange={(e) => {
                set_reason(e.target.value);
              }}
            />
          </div>
          {failure && (
            <Alert
              variant={
                failure.kind === 'already_terminated' ? 'warning' : 'danger'
              }
              data-testid="terminate-confirm-failure"
            >
              {failure.kind === 'already_terminated'
                ? t('employees.terminate.already_terminated_error')
                : failure.kind === 'entitlement_required'
                  ? t('employees.terminate.entitlement_required_error')
                  : failure.kind === 'validation'
                    ? failure.message
                    : t('employees.terminate.error_generic')}
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={is_submitting}
          >
            {t('employees.terminate.cancel_cta')}
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              void handle_confirm();
            }}
            disabled={is_submitting || termination_date === ''}
          >
            {onOpenFinalPay
              ? t('employees.terminate.continue_to_final_pay_cta')
              : t('employees.terminate.confirm_cta')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Shared "state passed from the confirm dialog to the drawer" — the two
 * fields the drawer needs to seed its own inputs from (termination_date +
 * reason). Exported so `employee_profile_screen.tsx` can hold this in
 * useState between the confirm dialog closing and the drawer opening.
 */
export interface TerminationDraft {
  termination_date: string;
  reason?: string;
}
